import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Throttle function to limit how often a function can be called
 */
function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  return function (...args: Parameters<T>) {
    const now = Date.now();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return func(...args);
  };
}

export interface UseIdleTimerOptions {
  /**
   * Timeout duration in milliseconds
   * @default 900000 (15 minutes)
   */
  timeout?: number;
  
  /**
   * Warning duration before timeout (ms)
   * @default 30000 (30 seconds)
   */
  warningTime?: number;
  
  /**
   * Callback when warning period starts
   */
  onWarning?: (remainingSeconds: number) => void;
  
  /**
   * Callback when user becomes idle
   */
  onIdle: () => void;
  
  /**
   * Events to listen for activity
   * @default ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
   */
  events?: string[];
  
  /**
   * Throttle delay for activity events (ms)
   * @default 1000
   */
  throttleDelay?: number;
}

/**
 * Hook to detect user inactivity and trigger logout
 * 
 * Features:
 * - Tracks mouse, keyboard, and touch activity
 * - Syncs activity across browser tabs via localStorage
 * - Throttles activity updates to prevent excessive writes
 * - Checks for idle timeout every second
 * - Returns an unthrottled resetTimer for explicit session continuation
 * 
 * @example
 * ```tsx
 * useIdleTimer({
 *   timeout: 15 * 60 * 1000, // 15 minutes
 *   onIdle: () => logout()
 * });
 * ```
 */
export const useIdleTimer = ({
  timeout = 15 * 60 * 1000, // 15 minutes default
  warningTime = 30 * 1000, // 30 seconds default
  onWarning,
  onIdle,
  events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'],
  throttleDelay = 1000,
}: UseIdleTimerOptions): { lastActivity: number; resetTimer: () => void } => {
  const STORAGE_KEY = 'oneportal_last_activity';
  const WARNING_SHOWN_KEY = 'oneportal_warning_shown';
  
  // Safety: Enforce minimum 30 minutes in production
  const MIN_TIMEOUT_PROD = 30 * 60 * 1000; // 30 minutes
  const isProduction = import.meta.env.PROD;
  const safeTimeout = isProduction && timeout < MIN_TIMEOUT_PROD ? MIN_TIMEOUT_PROD : timeout;
  
  const lastActivityRef = useRef<number>(0);
  if (lastActivityRef.current === 0) {
    const stored = localStorage.getItem(STORAGE_KEY);
    lastActivityRef.current = stored ? Number(stored) : Date.now();
  }

  const [warningShown, setWarningShown] = useState(false);
  const warningShownRef = useRef(warningShown);

  const onIdleRef = useRef(onIdle);
  const onWarningRef = useRef(onWarning);

  // Keep refs up to date
  useEffect(() => {
    onIdleRef.current = onIdle;
    onWarningRef.current = onWarning;
  }, [onIdle, onWarning]);

  useEffect(() => {
    warningShownRef.current = warningShown;
  }, [warningShown]);

  /**
   * Sync activity across browser tabs
   */
  const syncActivityAcrossTabs = useCallback((event: StorageEvent) => {
    if (event.key === STORAGE_KEY && event.newValue) {
      lastActivityRef.current = Number(event.newValue);
    }
  }, []);

  /**
   * Update last activity timestamp
   */
  const resetTimer = useCallback(() => {
    const currentTime = Date.now();
    lastActivityRef.current = currentTime;
    localStorage.setItem(STORAGE_KEY, currentTime.toString());
    warningShownRef.current = false;
    setWarningShown(false);
    localStorage.removeItem(WARNING_SHOWN_KEY);
  }, []);

  // Once the warning shows, only an explicit resetTimer() may extend the session.
  const handleActivity = useCallback(
    throttle(() => {
      if (!warningShownRef.current) resetTimer();
    }, throttleDelay),
    [resetTimer, throttleDelay]
  );

  /**
   * Check if user is idle
   */
  const checkIdleTimeout = useCallback(() => {
    const now = Date.now();
    const idleDuration = now - lastActivityRef.current;
    const timeUntilLogout = safeTimeout - idleDuration;
    
    // Show warning if approaching timeout
    if (timeUntilLogout <= warningTime && timeUntilLogout > 0 && !warningShownRef.current && onWarningRef.current) {
      console.log(`⚠️ Showing idle warning - ${Math.ceil(timeUntilLogout / 1000)}s remaining`);
      warningShownRef.current = true;
      setWarningShown(true);
      localStorage.setItem(WARNING_SHOWN_KEY, 'true');
      const remainingSeconds = Math.ceil(timeUntilLogout / 1000);
      onWarningRef.current(remainingSeconds);
    }
    
    // Trigger logout if timeout reached
    if (idleDuration >= safeTimeout) {
      console.log(`⏱️ Idle timeout reached (${idleDuration / 1000}s idle), triggering logout`);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(WARNING_SHOWN_KEY);
      onIdleRef.current();
    }
  }, [safeTimeout, warningTime]);

  /**
   * Set up event listeners and interval
   */
  useEffect(() => {
    // Check idle status every second
    const interval = setInterval(() => {
      checkIdleTimeout();
    }, 1000);

    // Listen for user activity
    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Sync across tabs
    window.addEventListener('storage', syncActivityAcrossTabs);
    
    // Check on window focus/blur
    window.addEventListener('blur', checkIdleTimeout);
    window.addEventListener('focus', checkIdleTimeout);

    return () => {
      clearInterval(interval);
      
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      
      window.removeEventListener('storage', syncActivityAcrossTabs);
      window.removeEventListener('blur', checkIdleTimeout);
      window.removeEventListener('focus', checkIdleTimeout);
    };
  }, [events, handleActivity, syncActivityAcrossTabs, checkIdleTimeout]);

  return { lastActivity: lastActivityRef.current, resetTimer };
};
